import { closest } from 'js/tools';

const CLASS_AUTOCOMPLETE_ENTRY_HIGHTLIGHT = 'highlight';
const CLASS_AUTOCOMPLETE_ENTRY = 'autocomplete-entry';
const CLASS_AUTOCOMPLETE_INPUT_VALID = 'valid';
const CLASS_AUTOCOMPLETE_SUGGESTIONS_SHOWN = 'input-autocomplete--opened';
const CLASS_SHOW_VALIDATION_MESSAGES = 'show-messages';
const EVENT_AUTOCOMPLETE_ENTRY_INVALID = 'autocomplete-entry-invalid';
const EVENT_AUTOCOMPLETE_ENTRY_VALID = 'autocomplete-entry-valid';
const SELECTOR_INPUT_ELEMENT = '.input-autocomplete__input input';
const SELECTOR_AUTOCOMPLETE_ENTRY = `.${CLASS_AUTOCOMPLETE_ENTRY}`;
const SELECTOR_AUTOCOMPLETE_ENTRY_CONTAINER = '.input-autocomplete__options';
const SELECTOR_AUTOCOMPLETE_INPUT_MESSAGES = '.input-autocomplete .input-messages';

export default node => {
    // DOM references
    const refs = {
        inputField: node.querySelector(SELECTOR_INPUT_ELEMENT),
        suggestionsContainer: node.querySelector(SELECTOR_AUTOCOMPLETE_ENTRY_CONTAINER),
        messages: node.querySelector(SELECTOR_AUTOCOMPLETE_INPUT_MESSAGES)
    };

    let highlightedSuggestion;
    let inputIsValid = false;

    function init() {
        const inputElement = refs.inputField;

        inputElement.addEventListener('blur', () => {
            hideSuggestions();
            if (inputIsValid || !hasValue()) {
                publishValue();
            } else {
                publishInvalidValue();
            }
        });
        inputElement.addEventListener('focus', () => {
            if (hasValue() && hasSuggestions()) {
                showSuggestions();
            }
        });
        inputElement.addEventListener('keyup', handleSpecialKeys);
        inputElement.addEventListener('input', clearInputValidMarker);
        // special handling for the enter key, it should be caught using keydown and swallowed to prevent form submit
        inputElement.addEventListener('keydown', e => {
            if (e.key === 'Enter' || e.key === 'NumpadEnter') {
                e.preventDefault();
            }
        });

        node.addEventListener('isValid', onValidInput);
        node.addEventListener('isInvalid', onInvalidInput);

        // Assume any value pre-filled on load is valid, any change will trigger validation anyway
        if (hasValue()) {
            markInputAsValid();
        }
    }

    function onValidInput(event) {
        const validationDetail = event?.detail;
        if (!validationDetail) {
            return;
        }

        clearSuggestions();
        showValidationMessages();

        switch (validationDetail.httpStatus) {
            case 400: // no matching zipcode or city found
                clearInputValidMarker();
                publishInvalidValue();
                return;
            case 204: // input is an exact match;
                markInputAsValid();
                publishValue();
                return;
            case 206: // partial match for zipcode
                markInputAsValid();
                publishValue();
                return;
            case 200: // response with suggestions
                if (isCurrentInputValid(validationDetail.suggestions)) {
                    markInputAsValid();
                    publishValue();
                } else {
                    clearInputValidMarker();
                    publishInvalidValue();
                }
                displaySuggestions(validationDetail.suggestions);
                break;
            default:
                break;
        }
    }

    function publishValue() {
        refs.inputField.dispatchEvent(
            new CustomEvent(EVENT_AUTOCOMPLETE_ENTRY_VALID, {
                detail: refs.inputField.value
            })
        );
    }

    function publishInvalidValue() {
        refs.inputField.dispatchEvent(
            new CustomEvent(EVENT_AUTOCOMPLETE_ENTRY_INVALID, {
                detail: refs.inputField.value
            })
        );
    }

    function onInvalidInput() {
        showValidationMessages();
        publishInvalidValue();
        clearSuggestions();
    }

    function handleSpecialKeys(event) {
        switch (event.key) {
            case 'Enter':
            case 'NumpadEnter':
                if (highlightedSuggestion) {
                    submitSuggestion(highlightedSuggestion);
                }
                break;
            case 'Up':
            case 'ArrowUp':
            case 'Down':
            case 'ArrowDown':
                handleSuggestionNavigation(event);
                break;
            case 'Escape':
            case 'Esc':
                clearSuggestions();
                break;
            default:
                if (!hasValue()) {
                    clearSuggestions();
                }
        }
    }

    function handleSuggestionNavigation(event) {
        const sibling = /Down$/i.test(event.key)
            ? highlightedSuggestion && highlightedSuggestion.nextElementSibling
            : highlightedSuggestion && highlightedSuggestion.previousElementSibling;
        const wrapSelector = /Down$/i.test(event.key) ? 'first-child' : 'last-child';

        if (!highlightedSuggestion || !sibling) {
            highlightedSuggestion = node.querySelector(`${SELECTOR_AUTOCOMPLETE_ENTRY}:${wrapSelector}`);
        } else if (sibling) {
            highlightedSuggestion = sibling;
        } else {
            return;
        }
        highlightSuggestion(highlightedSuggestion);
        preFillSuggestion(highlightedSuggestion);
    }

    function displaySuggestions(suggestions) {
        if (!suggestions.length) {
            return;
        }

        clearSuggestions();
        suggestions.forEach(suggestion => {
            const newSuggesionElement = createSuggestionElement(suggestion);
            refs.suggestionsContainer.appendChild(newSuggesionElement);
        });

        if (isInputFieldFocused()) {
            showSuggestions();
            hideValidationMessages();
        }
    }

    function createSuggestionElement(suggestion) {
        const newSuggesionElement = document.createElement('li');
        newSuggesionElement.classList.add(CLASS_AUTOCOMPLETE_ENTRY);

        newSuggesionElement.innerHTML = suggestion.displayName.replace(
            new RegExp(`${refs.inputField.value}`, 'i'),
            match => `<span class="emphasize">${match}</span>`
        );

        newSuggesionElement.addEventListener('mousemove', () => highlightSuggestion(newSuggesionElement));

        // hook mousedown and click to prevent ordering issues with the blur event handler
        newSuggesionElement.addEventListener('mousedown', () => submitSuggestion(newSuggesionElement));
        newSuggesionElement.addEventListener('click', () => submitSuggestion(newSuggesionElement));

        return newSuggesionElement;
    }

    function submitSuggestion(suggestion) {
        if (suggestion) {
            preFillSuggestion(suggestion);
            clearSuggestions();
            publishValue();
        }
    }

    function highlightSuggestion(suggestion) {
        const suggestionEntries = node.querySelectorAll(SELECTOR_AUTOCOMPLETE_ENTRY);
        suggestionEntries.forEach(element => {
            element.classList.remove(CLASS_AUTOCOMPLETE_ENTRY_HIGHTLIGHT);
        });

        if (suggestion) {
            suggestion.classList.add(CLASS_AUTOCOMPLETE_ENTRY_HIGHTLIGHT);
        }
    }

    function preFillSuggestion(suggestion) {
        if (suggestion) {
            markInputAsValid();
            refs.inputField.value = suggestion.innerText;
        }
    }

    function clearSuggestions() {
        highlightedSuggestion = undefined;
        refs.suggestionsContainer.innerHTML = '';
        hideSuggestions();
    }

    function markInputAsValid() {
        node.classList.add(CLASS_AUTOCOMPLETE_INPUT_VALID);
        node.setAttribute('data-state', 'success');
        inputIsValid = true;

        if (!isInputFieldFocused()) {
            publishValue();
        }
    }

    function clearInputValidMarker() {
        node.classList.remove(CLASS_AUTOCOMPLETE_INPUT_VALID);
        node.setAttribute('data-state', 'error');
        inputIsValid = false;
    }

    function showSuggestions() {
        node.classList.add(CLASS_AUTOCOMPLETE_SUGGESTIONS_SHOWN);
    }

    function hideSuggestions() {
        node.classList.remove(CLASS_AUTOCOMPLETE_SUGGESTIONS_SHOWN);
    }

    function showValidationMessages() {
        refs.messages.classList.add(CLASS_SHOW_VALIDATION_MESSAGES);
    }

    function hideValidationMessages() {
        refs.messages.classList.remove(CLASS_SHOW_VALIDATION_MESSAGES);
    }

    function getValue() {
        return refs.inputField.value;
    }

    function reset() {
        node.classList.remove('has-content');
        node.classList.remove('has-remark');
        node.removeAttribute('data-state');
        clearInputValidMarker();
        hideValidationMessages();
        const inputWrapper = closest(refs.inputField, 'input-text');
        inputWrapper.jsApi.reset();
    }

    function hasValue() {
        return !!refs.inputField.value.trim();
    }

    function hasSuggestions() {
        return !!refs.suggestionsContainer.innerHTML.trim();
    }

    function isInputFieldFocused() {
        return document.activeElement === refs.inputField;
    }

    function isCurrentInputValid(suggestions) {
        return suggestions?.some(s => s.isExactMatch);
    }

    init();

    return {
        reset,
        getValue,
        hasValue,
        hasSuggestions
    };
};
